import api from '../../../config/axios';
import type { CalibrationTrendData } from '../types';
import type { Calibration } from '../../../types/api';

export interface DashboardStatisticsResponse {
  totalCalibrated: number;
  totalPassed: number;
  totalFailed: number;
  trends?: CalibrationTrendData[];
}

export const DashboardService = {
  /**
   * Get dashboard statistics from sensors and calibrations APIs
   * @param year - Year filter
   * @param month - Month filter (optional)
   */
  getStatistics: async (year: string, month?: string): Promise<DashboardStatisticsResponse> => {
    try {
      // First try the sensors statistics endpoint (backend provides aggregated stats)
      try {
        const statsResponse = await api.get('/sensors/statistics/overview');
        const stats = statsResponse.data || {};
        const breakdown: Record<string, number> = stats.calibration_status_breakdown || {};
        const totalCalibrations = stats.calibration_overview?.total_calibrations ?? 0;

        const passedKeys = ['Calibrated_Pass', 'QA_Pass', 'Passed', 'pass', 'approved', 'success'];
        const failedKeys = ['Calibrated_Fail', 'QA_Fail', 'Failed', 'fail', 'rejected', 'error'];

        const totalPassed = Object.entries(breakdown)
          .filter(([key]) => passedKeys.some(p => key.toLowerCase().includes(p.toLowerCase())))
          .reduce((sum, [, value]) => sum + Number(value || 0), 0);

        const totalFailed = Object.entries(breakdown)
          .filter(([key]) => failedKeys.some(f => key.toLowerCase().includes(f.toLowerCase())))
          .reduce((sum, [, value]) => sum + Number(value || 0), 0);

        return {
          totalCalibrated: totalCalibrations,
          totalPassed,
          totalFailed,
        };
      } catch (statsErr) {
        // If the aggregated endpoint fails, fall back to manual calculation below
        console.warn('Falling back to manual stats calculation:', statsErr);
      }

      // Helper to parse calibration date safely (fallback to updated_date or now if missing)
      const getCalibrationDate = (cal: Calibration) =>
        new Date(cal.created_date || cal.updated_date || Date.now());

      // Get all calibrations to count total, passed, and failed
      const calibrationsResponse = await api.get<Calibration[]>('/calibrations', {
        params: {
          limit: 10000, // Get all calibrations
        },
      });

      const calibrations = calibrationsResponse.data;

      // Filter by year and month if provided
      let filteredCalibrations = calibrations;
      
      if (year) {
        filteredCalibrations = filteredCalibrations.filter((cal) => {
          const calDate = getCalibrationDate(cal);
          return calDate.getFullYear().toString() === year;
        });
      }

      if (month && month !== 'All Months') {
        const monthIndex = [
          'January', 'February', 'March', 'April', 'May', 'June',
          'July', 'August', 'September', 'October', 'November', 'December'
        ].indexOf(month);
        
        if (monthIndex !== -1) {
          filteredCalibrations = filteredCalibrations.filter((cal) => {
            const calDate = getCalibrationDate(cal);
            return calDate.getMonth() === monthIndex;
          });
        }
      }

      // Count total calibrated sensors (unique sensors that have calibrations)
      const calibratedSensorIds = new Set(
        filteredCalibrations
          .map((cal) => cal.sensor_id)
          .filter((id): id is string => !!id)
      );
      const totalCalibrated = calibratedSensorIds.size;

      // Get all calibration results to check status
      let allResults: any[] = [];
      try {
        const resultsResponse = await api.get('/results', {
          params: { limit: 10000 }
        });
        allResults = Array.isArray(resultsResponse.data) ? resultsResponse.data : [];
      } catch (err) {
        console.warn('Could not fetch calibration results:', err);
      }

      // Create a map of calibration_id to result status
      const calibrationResultStatus = new Map<string, string>();
      allResults.forEach((result: any) => {
        if (result.calibration_id && result.status) {
          calibrationResultStatus.set(result.calibration_id, result.status);
        }
      });

      // Get all sensors to check their status directly from sensors API
      let allSensors: any[] = [];
      try {
        const sensorsResponse = await api.get('/sensors', {
          params: { 
            limit: 10000,
            include: 'calibrations'
          }
        });
        // Handle both array response and paginated response
        if (Array.isArray(sensorsResponse.data)) {
          allSensors = sensorsResponse.data;
        } else if (sensorsResponse.data?.data) {
          allSensors = sensorsResponse.data.data;
        }
      } catch (err) {
        console.warn('Could not fetch sensors:', err);
      }

      // Filter sensors that have calibrations in the filtered date range
      const sensorsWithCalibrations = allSensors.filter((sensor: any) => {
        return calibratedSensorIds.has(sensor.id);
      });

      let totalPassed = 0;
      let totalFailed = 0;

      // Check each sensor's status
      sensorsWithCalibrations.forEach((sensor: any) => {
        let status = '';
        
        // First check sensor status directly
        if (sensor.status) {
          status = sensor.status.toLowerCase();
        }
        
        // If sensor doesn't have status, check its latest calibration
        if (!status && sensor.calibrations && Array.isArray(sensor.calibrations) && sensor.calibrations.length > 0) {
          // Get the latest calibration
          const latestCal = sensor.calibrations
            .filter((cal: Calibration) => {
              const calDate = getCalibrationDate(cal);
              if (year && calDate.getFullYear().toString() !== year) return false;
              if (month && month !== 'All Months') {
                const monthIndex = [
                  'January', 'February', 'March', 'April', 'May', 'June',
                  'July', 'August', 'September', 'October', 'November', 'December'
                ].indexOf(month);
                if (monthIndex !== -1 && calDate.getMonth() !== monthIndex) return false;
              }
              return true;
            })
            .sort((a: Calibration, b: Calibration) => 
              getCalibrationDate(b).getTime() - getCalibrationDate(a).getTime()
            )[0];

          if (latestCal) {
            // Check calibration status
            status = latestCal.status?.toLowerCase() || '';
            
            // If no status in calibration, check result status
            if (!status && calibrationResultStatus.has(latestCal.id)) {
              status = calibrationResultStatus.get(latestCal.id)?.toLowerCase() || '';
            }

            // Also check result object if available
            if (!status && latestCal.result) {
              if (typeof latestCal.result === 'object' && latestCal.result !== null) {
                status = (latestCal.result as any).status?.toLowerCase() || '';
              }
            }
          }
        }

        // Count based on status dynamically (no hardcoded lists)
        if (status) {
          if (status.includes('pass')) {
            totalPassed++;
          } else if (status.includes('fail')) {
            totalFailed++;
          }
        }
      });

      return {
        totalCalibrated,
        totalPassed,
        totalFailed,
      };
    } catch (error) {
      console.error('Error fetching dashboard statistics:', error);
      // Return default values on error
      // Do not hardcode; on failure return zeros so UI shows empty state
      return {
        totalCalibrated: 0,
        totalPassed: 0,
        totalFailed: 0,
      };
    }
  },

  /**
   * Get calibration trends data
   * @param year - Year filter
   * @param month - Month filter (optional)
   */
  getTrends: async (year: string, month?: string): Promise<CalibrationTrendData[]> => {
    try {
      const getCalibrationDate = (cal: Calibration) =>
        new Date(cal.created_date || cal.updated_date || Date.now());
      const monthNames = [
        'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
      ];

      // Fetch sensors with calibrations included (single call)
      const sensorsResponse = await api.get('/sensors', {
        params: { limit: 10000, include: 'calibrations' },
      });
      let sensors: any[] = [];
      if (Array.isArray(sensorsResponse.data)) {
        sensors = sensorsResponse.data;
      } else if (sensorsResponse.data?.data) {
        sensors = sensorsResponse.data.data;
      }

      // Prepare base structure
      const trends = monthNames.map((name) => ({
        name,
        'Total Calibrations': 0,
        'Passed': 0,
        'Failed': 0,
      }));

      // Status detection helper
      const isPass = (status: string) => status.toLowerCase().includes('pass');
      const isFail = (status: string) => status.toLowerCase().includes('fail');

      sensors.forEach((sensor) => {
        if (!sensor.calibrations || !Array.isArray(sensor.calibrations)) return;

        sensor.calibrations.forEach((cal: Calibration) => {
          const calDate = getCalibrationDate(cal);
          if (isNaN(calDate.getTime())) return;

          // Year filter
          if (year && calDate.getFullYear().toString() !== year) return;

          // Month filter (if specific month chosen)
          const calMonthIndex = calDate.getMonth();
          if (month && month !== 'All Months') {
            const monthIndex = [
              'January', 'February', 'March', 'April', 'May', 'June',
              'July', 'August', 'September', 'October', 'November', 'December'
            ].indexOf(month);
            if (monthIndex !== -1 && calMonthIndex !== monthIndex) return;
          }

          const bucket = trends[calMonthIndex];
          if (!bucket) return;

          bucket['Total Calibrations'] += 1;

          // Determine status from calibration or result
          let status = cal.status?.toLowerCase() || '';
          if (!status && cal.result && typeof cal.result === 'object') {
            status = (cal.result as any).status?.toLowerCase() || '';
          }

          if (status) {
            if (isPass(status)) bucket['Passed'] += 1;
            else if (isFail(status)) bucket['Failed'] += 1;
          }
        });
      });

      return trends;
    } catch (error) {
      console.error('Error fetching calibration trends:', error);
      // Return default/empty data on error
      return [
        { name: 'Jan', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Feb', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Mar', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Apr', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'May', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Jun', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Jul', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Aug', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Sep', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Oct', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Nov', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
        { name: 'Dec', 'Total Calibrations': 0, 'Passed': 0, 'Failed': 0 },
      ];
    }
  },
};

